import { alpha, Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material';
import { Zap as Bolt, ChevronUp as ExpandLess, ChevronDown as ExpandMore } from 'lucide-react';
import React, { useState } from 'react';

import type { AgentAction } from './types';

interface AgentActionPanelProps {
    actions: AgentAction[];
    isExpanded: boolean;
    onToggle: () => void;
    onActionSelect: (text: string) => void;
}

export const AgentActionPanel: React.FC<AgentActionPanelProps> = ({
    actions,
    isExpanded,
    onToggle,
    onActionSelect,
}) => {
    const theme = useTheme();

    return (
        <Box sx={{ width: '100%', height: 'auto', display: 'grid', flexDirection: 'column', p: 1, pt: 0, mt: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: isExpanded ? 1 : 0,
                    mt: 0,
                    ml: 1,
                    cursor: 'pointer',
                    // Divider entfernt
                }}
                onClick={onToggle}
            >
                <Bolt size={20} color={theme.palette.primary.main} />
                <Typography
                    variant="h6"
                    fontWeight={600}
                    color="text.primary"
                    sx={{
                        transition: 'color 0.2s ease-in-out',
                        '&:hover': { color: 'primary.main' },
                    }}
                >
                    Agent in Action
                </Typography>
                {isExpanded ? (
                    <ExpandLess size={20} color={theme.palette.primary.main} style={{ marginLeft: 'auto' }} />
                ) : (
                    <ExpandMore size={20} color={theme.palette.primary.main} style={{ marginLeft: 'auto' }} />
                )}
            </Box>

            {isExpanded && (
                <Stack
                    spacing={0.5}
                    sx={{
                        flex: isExpanded ? 1 : 0,
                        overflowY: 'auto',
                        pt: 1,
                        pb: 1,
                        px: 1,
                        maxHeight: isExpanded ? '300px' : 0,
                        height: isExpanded ? '100%' : 0,
                        width: '100%',
                        transition: 'all 0.3s ease-in-out',
                    }}
                >
                    {actions.map((action) => (
                        <Box
                            key={action.id}
                            onClick={() => onActionSelect(action.actionText)}
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                bgcolor: alpha(theme.palette.background.paper, 0.7),
                                width: '100%',
                                maxWidth: '100%',
                                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                borderRadius: 1,
                                p: 1,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    borderColor: theme.palette.primary.main,
                                    transform: 'translateX(4px)',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 0.25,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={500}
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1,
                                                fontSize: '0.875rem',
                                                color: theme.palette.primary.main,
                                            }}
                                        >
                                            {action.title}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.75rem',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {action.description}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default AgentActionPanel;
